import { ref } from 'vue'

export const pushEnabled = ref(false)
export const pushStage = ref('')
export const pushFailure = ref('')
let binding = null
let registration = null
let currentApi = null
let epoch = 0
let unread = 0
let updateChain = Promise.resolve()
const bounded = (promise, label, ms = 20000) => {
  let timer
  return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(label)), ms) })]).finally(() => clearTimeout(timer))
}
export const pushSupport = () => {
  if (!window.isSecureContext) return '请使用 HTTPS 地址打开，HTTP 无法启用手机通知。'
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (ios && !navigator.standalone && !window.matchMedia('(display-mode: standalone)').matches) return 'iPhone / iPad 请先使用 Safari 分享菜单“添加到主屏幕”，再从桌面图标打开以启用通知。'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return '此浏览器暂不支持推送。iPhone 请使用 iOS 16.4 或更高版本，先从 Safari 添加到主屏幕，再从桌面图标打开。'
  if (Notification.permission === 'denied') return '通知已被系统拒绝，请先在浏览器或系统设置中允许通知。'
  return ''
}
const workerMessage = async (target, value) => {
  if (!target) return
  await new Promise(resolve => {
    const channel = new MessageChannel()
    const timer = setTimeout(resolve, 3000)
    channel.port1.onmessage = () => { clearTimeout(timer); channel.port1.close(); resolve() }
    target.postMessage(value, [channel.port2])
  })
}
const sendBinding = () => {
  const captured = { type: 'chat-binding', binding: binding ? { id: binding.id, updated: Date.now() } : null, unread }
  updateChain = updateChain.catch(() => {}).then(() => workerMessage(registration?.active || navigator.serviceWorker?.controller, captured))
  return updateChain
}
export const updateChatBadge = count => {
  unread = Math.max(0, Number(count) || 0)
  try {
    const promise = unread ? navigator.setAppBadge?.(unread) : navigator.clearAppBadge?.()
    promise?.catch(() => {})
  } catch (_) { /* Optional platform API. */ }
  if (registration) sendBinding()
}
const decodeKey = value => Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4)), char => char.charCodeAt(0))
const bind = async (api, sub, version) => {
  const result = await api('/push/subscription', { method: 'POST', body: JSON.stringify(sub.toJSON()) })
  if (version !== epoch) {
    // A logout or account switch raced registration; the old binding stays hidden.
    return
  }
  binding = result
  pushEnabled.value = true
  pushFailure.value = ''
  await sendBinding()
}
export const initializeChatPush = async api => {
  currentApi = api
  const version = ++epoch
  if (!window.isSecureContext || !('serviceWorker' in navigator)) return
  try {
    registration = await navigator.serviceWorker.register('/chat-sw.js', { scope: '/', updateViaCache: 'none' })
    registration = await bounded(navigator.serviceWorker.ready, '通知组件未就绪，请刷新页面重试。')
    if (version !== epoch) return
    const sub = await registration.pushManager?.getSubscription()
    if (sub && Notification.permission === 'granted') await bind(api, sub, version)
  } catch (err) { pushFailure.value = err.message || '通知订阅恢复失败，请重新启用。' }
}
export const enableChatPush = async () => {
  const reason = pushSupport()
  if (reason) throw new Error(reason)
  // Must run immediately inside the button's user gesture, before network work.
  pushStage.value = '等待系统通知授权'
  if (await Notification.requestPermission() !== 'granted') throw new Error('尚未允许通知，请在系统设置中开启。')
  const version = epoch
  if (!registration) {
    await navigator.serviceWorker.register('/chat-sw.js', { scope: '/', updateViaCache: 'none' })
    registration = await bounded(navigator.serviceWorker.ready, '通知组件未就绪，请刷新页面重试。')
  }
  pushStage.value = '正在连接浏览器推送服务'
  const conf = await currentApi('/push/config')
  let sub = await registration.pushManager.getSubscription()
  const key = decodeKey(conf.public_key)
  if (sub?.options?.applicationServerKey && btoa(String.fromCharCode(...new Uint8Array(sub.options.applicationServerKey))) !== btoa(String.fromCharCode(...key))) {
    await sub.unsubscribe(); sub = null
  }
  sub = sub || await bounded(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }),
    '浏览器推送服务连接超时，尚未启用。请检查网络；Android Chrome 依赖 Google 推送服务。切换网络后重试。', 25000)
  pushStage.value = '正在绑定本设备账号'
  await bind(currentApi, sub, version)
  pushStage.value = ''
}
export const inspectChatPush = async () => {
  const sub = await registration?.pushManager?.getSubscription()
  const permission = typeof Notification === 'undefined' ? '不支持' : Notification.permission
  const status = binding ? await currentApi(`/push/status/${binding.id}`) : null
  if (status && !status.active) pushEnabled.value = false
  return { secure: window.isSecureContext, permission, installed: !!navigator.standalone || window.matchMedia('(display-mode: standalone)').matches,
    subscribed: !!sub, bound: !!binding && !!status?.active, badgeSupported: 'setAppBadge' in navigator, unread, ...status }
}
export const testChatPush = async () => {
  if (!binding || !pushEnabled.value) throw new Error('请先启用本设备通知。')
  await sendBinding()
  return currentApi(`/push/test/${binding.id}`, { method: 'POST' })
}
export const disableChatPush = async () => {
  ++epoch
  if (binding) await currentApi(`/push/subscription/${binding.id}`, { method: 'DELETE' })
  binding = null
  pushEnabled.value = false
  const sub = await registration?.pushManager.getSubscription()
  await sub?.unsubscribe()
  await sendBinding()
}
export const clearChatPushBinding = () => {
  ++epoch
  binding = null
  unread = 0
  pushEnabled.value = false
  sendBinding()
  updateChatBadge(0)
}
